import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import useCreateProvidedJobPostList from '../api/useCreateProvidedJobPostList';
import { showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providedJobPostIds?: number[];
  onCreated?: () => void;
}

export default function CreateListDialog({
  open,
  onOpenChange,
  providedJobPostIds,
  onCreated,
}: Props) {
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const { mutateAsync: createList, isPending } = useCreateProvidedJobPostList();

  const handleSubmit = async () => {
    if (!name.trim()) return;
    const trimmedName = name.trim();
    try {
      await createList({
        name: trimmedName,
        isPublic,
        providedJobPostIds,
      });
      setName('');
      setIsPublic(false);
      onOpenChange(false);
      showSuccessToast(
        providedJobPostIds?.length
          ? `「${trimmedName}」を作成して求人を追加しました`
          : `「${trimmedName}」を作成しました`
      );
      onCreated?.();
    } catch {
      alert('リストの作成に失敗しました');
    }
  };

  const handleClose = () => {
    setName('');
    setIsPublic(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[640px] rounded-sm">
        <DialogHeader>
          <DialogTitle>リストを新規作成</DialogTitle>
        </DialogHeader>

        <div className="space-y-md p-lg">
          <div>
            <label htmlFor="create-list-name" className="mb-xs block text-sm font-bold">
              リスト名
            </label>
            <input
              id="create-list-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="リスト名を入力してください"
              maxLength={15}
              className="w-[240px] rounded-sm border border-solid border-border px-3 py-2 text-sm focus:border-primary focus:outline-hidden"
              autoFocus
            />
          </div>

          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="visibility"
                checked={!isPublic}
                onChange={() => setIsPublic(false)}
                className="accent-primary"
              />
              <span className="text-sm">非公開</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="visibility"
                checked={isPublic}
                onChange={() => setIsPublic(true)}
                className="accent-primary"
              />
              <span className="text-sm">公開</span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <div className="flex gap-2">
            <Button color="secondary" size="md" onClick={handleClose}>
              キャンセル
            </Button>
            <Button
              color="primary"
              size="md"
              onClick={handleSubmit}
              disabled={!name.trim() || isPending}
            >
              {isPending ? '作成中...' : providedJobPostIds?.length ? '追加して新規作成' : '作成'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
